import { CardSpacer } from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { AccountErrorFragment } from "@saleor/fragments/types/AccountErrorFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useCustomerOrderSearch from "@saleor/searches/useCustomerOrderSearch";
import useCustomerRedeemedCoupon from "@saleor/searches/useCustomerRedeemedCoupon";
import {
  CouponRedemptionSortField,
  CouponRedemptionStatusEnum,
  OrderDirection,
  OrderSortField
} from "@saleor/types/globalTypes";
import React from "react";

import { getUserName, maybe } from "../../../misc";
import { CustomerDetails_user } from "../../types/CustomerDetails";
import CustomerAddresses from "../CustomerAddresses";
import CustomerDetails from "../CustomerDetails";
import CustomerInfo from "../CustomerInfo";
import CustomerOrders from "../CustomerOrders";
import CustomerRedeemedCoupons from "../CustomerRedeemedCoupons/CustomerRedeemedCoupons";
import CustomerStats from "../CustomerStats";
import CustomerStatus from "../CustomerStatus";

export interface CustomerDetailsPageFormData {
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  note: string;
}

export interface CustomerDetailsPageProps {
  customer: CustomerDetails_user;
  customerAccountStatus: string;
  disabled: boolean;
  errors: AccountErrorFragment[];
  saveButtonBar: ConfirmButtonTransitionState;
  onBack: () => void;
  onSubmit: (data: CustomerDetailsPageFormData) => SubmitPromise;
  onViewAllOrdersClick: () => void;
  onRowClick: (id: string) => void;
  onAddressManageClick: () => void;
  onDelete: () => void;
}

const CustomerDetailsPage: React.FC<CustomerDetailsPageProps> = ({
  customer,
  disabled,
  errors,
  saveButtonBar,
  customerAccountStatus,
  onBack,
  onSubmit,
  onViewAllOrdersClick,
  onRowClick,
  onAddressManageClick,
  onDelete
}: CustomerDetailsPageProps) => {
  const ordersInitialSearchData = {
    customer: customer?.email,
    first: 5,
    query: "",
    sortBy: {
      direction: OrderDirection.DESC,
      field: OrderSortField.CREATION_DATE
    }
  };

  const {
    loadMore: loadMoreOrders,
    result: ordersQueryData
  } = useCustomerOrderSearch({
    variables: {
      ...ordersInitialSearchData
    }
  });

  const couponsRedeemedInitialSearchData = {
    first: 5,
    query: "",
    sortBy: {
      direction: OrderDirection.DESC,
      field: CouponRedemptionSortField.REDEEMED_AT
    },
    status: [CouponRedemptionStatusEnum.REDEEMED],
    userId: customer?.id
  };

  const {
    loadMore: loadMoreRedeemedCoupons,
    result: redeemedCouponsQueryData
  } = useCustomerRedeemedCoupon({
    variables: {
      ...couponsRedeemedInitialSearchData
    }
  });

  return (
    <>
      <Form
        initial={{
          email: maybe(() => customer.email, ""),
          firstName: maybe(() => customer.firstName, ""),
          isActive: maybe(() => customer.isActive, false),
          lastName: maybe(() => customer.lastName, ""),
          note: maybe(() => customer.note, "")
        }}
        onSubmit={onSubmit}
        confirmLeave
      >
        {({ change, data, hasChanged, submit }) => {
          const breadcrumbDirectory = [
            {
              name: "Customers",
              url: "/customers"
            },
            {
              name: getUserName(customer, true)
            }
          ];
          return (
            <Container>
              <PageBreadcrumbNavigation items={breadcrumbDirectory} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "10px"
                }}
              >
                <PageBackNavigation onBack={onBack} />
                <PageHeader title={getUserName(customer, true)} />
                {customer && (
                  <CustomerStatus status={customer.newCustomerStatus} />
                )}
              </div>

              <Grid>
                <div>
                  <CustomerDetails
                    customer={customer}
                    data={data}
                    disabled={disabled}
                    errors={errors}
                    onChange={change}
                  />
                  <CardSpacer />
                  <CustomerInfo
                    data={data}
                    disabled={disabled}
                    errors={errors}
                    onChange={change}
                  />
                  <CardSpacer />
                  <CustomerOrders
                    orders={maybe(() =>
                      ordersQueryData.data.search.edges.map(edge => edge.node)
                    )}
                    onViewAllOrdersClick={onViewAllOrdersClick}
                    onRowClick={onRowClick}
                    hasMore={ordersQueryData.data?.search?.pageInfo.hasNextPage}
                    loading={ordersQueryData.loading}
                    onFetchMore={loadMoreOrders}
                  />
                  <div
                    style={{
                      display:
                        redeemedCouponsQueryData.data?.search?.edges.length ===
                        0
                          ? "none"
                          : "block"
                    }}
                  >
                    <CardSpacer />
                    <CustomerRedeemedCoupons
                      redeemedCoupons={maybe(() =>
                        redeemedCouponsQueryData.data.search.edges.map(
                          edge => edge.node
                        )
                      )}
                      hasMore={
                        redeemedCouponsQueryData.data?.search?.pageInfo
                          .hasNextPage
                      }
                      loading={redeemedCouponsQueryData.loading}
                      onFetchMore={loadMoreRedeemedCoupons}
                    />
                  </div>
                </div>
                <div>
                  <CustomerAddresses
                    customer={customer}
                    disabled={disabled}
                    onAddressManageClick={onAddressManageClick}
                  />
                  <CardSpacer />
                  <CustomerStats customer={customer} />
                </div>
              </Grid>
              <SaveButtonBar
                disabled={disabled || !hasChanged}
                state={saveButtonBar}
                onSave={submit}
                onCancel={onBack}
                onDelete={customerAccountStatus === "DELETED" ? null : onDelete}
              />
            </Container>
          );
        }}
      </Form>
    </>
  );
};
CustomerDetailsPage.displayName = "CustomerDetailsPage";
export default CustomerDetailsPage;
